import { useCallback, useEffect, useRef } from 'react';
import usePlanStore, {
  TVConfig,
} from '../../../../../../../../../../../store/planStore';

const useHandleUpdateDataRows = (tvConfig?: TVConfig) => {
  const { selectedCountry, updateTVConfig } = usePlanStore();

  const latestTvConfigRef = useRef<TVConfig | undefined>(tvConfig);

  useEffect(() => {
    if (tvConfig) {
      latestTvConfigRef.current = tvConfig;
    }
  }, [tvConfig]);

  const handleCellUpdate = useCallback(
    (
      rowIndex: number,
      colIndex: number,
      newValue: number,
      updateCellValue: (newValue: number) => void
    ) => {
      updateCellValue(newValue);

      const latestTvConfig = latestTvConfigRef.current;

      if (!latestTvConfig || !latestTvConfig.grpTable) return;

      const prevWeeks = latestTvConfig.grpTable.weeks ?? [];
      const targetWeekIndex = colIndex - 2;

      const updatedWeeks = prevWeeks.map((week, weekIndex) => {
        if (weekIndex === targetWeekIndex) {
          return {
            ...week,
            copy: {
              ...week.copy,
              value: week.copy.value.map((cell, index) =>
                index === rowIndex ? { ...cell, value: newValue } : cell
              ),
            },
          };
        }
        return { ...week };
      });

      const groupedWeeks = updatedWeeks.reduce((acc, week, index) => {
        const key = week.weekNumber;
        if (!acc[key]) {
          acc[key] = { indices: [] as number[], sum: 0 };
        }
        acc[key].indices.push(index);
        const weekSum = week.copy.value.reduce(
          (sum, cell) => sum + cell.value,
          0
        );
        acc[key].sum += weekSum;
        return acc;
      }, {} as Record<string, { indices: number[]; sum: number }>);

      const updatedColumnSums = new Array(updatedWeeks.length).fill(0);
      Object.entries(groupedWeeks).forEach(([_, group]) => {
        const targetIndex = group.indices[0];
        updatedColumnSums[targetIndex] = group.sum;
      });

      const updatedPlanTotals =
        updatedWeeks[0]?.copy.value.map((_, rowIdx) =>
          updatedWeeks.reduce(
            (sum, week) => sum + (week.copy.value[rowIdx]?.value ?? 0),
            0
          )
        ) ?? [];

      const updatedGrandTotal = updatedPlanTotals.reduce(
        (sum, value) => sum + value,
        0
      );

      updateTVConfig(selectedCountry!, {
        ...latestTvConfig,
        grpTable: {
          weeks: updatedWeeks,
          columnTotals: updatedColumnSums,
          planTotals: updatedPlanTotals,
          grandTotal: updatedGrandTotal,
        },
      });
    },
    [selectedCountry, updateTVConfig]
  );

  return { handleCellUpdate };
};

export default useHandleUpdateDataRows;
